import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
} from '@backstage/theme';
import logoIot from '../components/Root/logo/logo-iot.png';

/**
 * Indiana Office of Technology theme (IND-40).
 *
 * Every value below is lifted from the live in.gov agency-2026 stylesheets
 * (a custom Bootstrap 5 build) plus the IOT agency color sheet:
 *   https://www.in.gov/application/themes/agency-2026/css/styles-dotcms.dotsass
 *   https://www.in.gov/application/themes/agency-2026/css/agency-colors/iot.dotsass
 * See docs/architecture/indiana-iot-theme.md for the token-by-token mapping
 * and the list of things that could not be mirrored 1:1 in Backstage.
 */
export const iot = {
  navy: '#051733', // nav#header_top, body a, .btn-primary, table th
  navyHover: '#071f46', // body a:hover
  navyDeep: '#030f20', // header gradient stop
  green: '#2e8438', // .btn-secondary, .btn-primary hover — white on it is 4.65:1 (AA)
  greenBright: '#3ca748', // header border-bottom accent; 5.79:1 on navy (AA)
  greenSuccess: '#208646', // .success.button — 4.57:1 on white (AA)
  link: '#1779ba', // --bs-link-color — 4.65:1 on white (AA)
  linkHover: '#126195', // --bs-link-hover-color
  alert: '#ca4a36', // .alert.button — 4.58:1 on white (AA)
  warning: '#9e6b00', // .warning text tone — 4.57:1 on white (AA); #eb9e00 (2.21:1) fails AA so is only a hover bg on in.gov
  text: '#333333', // --bs-body-color
  muted: '#767676', // --bs-secondary — 4.50:1 on white (AA, edge)
  surface: '#fefefe', // --bs-body-bg, --bs-card-bg
  surfaceAlt: '#f4f4f4', // panels / callouts
  coolGray: '#e5e7ea', // .agency-spotlight bg
  border: '#e6e6e6', // --bs-card-border-color
  rule: '#dddddd', // .section-header border-bottom
  footer: '#1c1c1c', // #footer_global
  radius: 10, // --bs-border-radius, --bs-card-border-radius, --bs-btn-border-radius
  headingFont: "'Readex Pro', 'Open Sans', 'Helvetica', Arial, sans-serif",
  bodyFont: "'Rethink Sans', 'Helvetica', Arial, sans-serif",
} as const;

// in.gov headers are flat blocks (no decorative burst). genPageTheme always
// prepends the shape to the gradient, so pass `none` as the shape layer.
const flat = 'none';
const header = genPageTheme({ colors: [iot.navy, iot.navyDeep], shape: flat });

export const indianaIot = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: { main: iot.navy, contrastText: iot.surface },
      secondary: { main: iot.green, contrastText: iot.surface },
      error: { main: iot.alert },
      warning: { main: iot.warning },
      info: { main: iot.link },
      success: { main: iot.greenSuccess },
      text: { primary: iot.text, secondary: iot.muted },
      divider: iot.border,
      background: {
        default: iot.surface,
        paper: '#ffffff',
      },
      link: iot.link,
      linkHover: iot.linkHover,
      banner: {
        info: iot.link,
        error: iot.alert,
        text: iot.navy,
        link: iot.link,
      },
      errorBackground: '#fbeeec',
      warningBackground: '#fff7e0',
      infoBackground: '#eaf3fa',
      navigation: {
        background: iot.navy,
        indicator: iot.greenBright,
        color: iot.surface,
        selectedColor: iot.surface,
        navItem: {
          hoverBackground: iot.navyHover,
        },
        // Keys consumed by Root.tsx / revenium.ts conventions — kept so the
        // sidebar code paths work unchanged when this theme is active.
        sidebarGradient: `linear-gradient(180deg, ${iot.navy} 0%, ${iot.navyDeep} 100%)`,
        groupSeparatorColor: 'rgba(255, 255, 255, 0.12)',
      },
      // Consumed by components/Root/LogoFull.tsx and LogoIcon.tsx.
      branding: {
        logoFullSrc: logoIot,
        logoFullAlt: 'Indiana Office of Technology',
        logoFullWide: true,
        iconColor: iot.greenBright,
      },
    },
  }),
  defaultPageTheme: 'home',
  fontFamily: iot.bodyFont,
  htmlFontSize: 16,
  typography: {
    htmlFontSize: 16,
    fontFamily: iot.bodyFont,
    h1: {
      fontFamily: iot.headingFont,
      fontSize: '2rem',
      fontWeight: 600,
      marginBottom: 8,
    },
    h2: {
      fontFamily: iot.headingFont,
      fontSize: '1.5rem',
      fontWeight: 600,
      marginBottom: 8,
    },
    h3: {
      fontFamily: iot.headingFont,
      fontSize: '1.1875rem',
      fontWeight: 600,
      marginBottom: 6,
    },
    h4: {
      fontFamily: iot.headingFont,
      fontSize: '1.125rem',
      fontWeight: 600,
      marginBottom: 6,
    },
    h5: {
      fontFamily: iot.headingFont,
      fontSize: '1rem',
      fontWeight: 700,
      marginBottom: 4,
    },
    h6: {
      fontFamily: iot.headingFont,
      fontSize: '0.875rem',
      fontWeight: 700,
      marginBottom: 2,
    },
  },
  pageTheme: {
    home: header,
    documentation: header,
    tool: header,
    service: header,
    website: header,
    library: header,
    other: header,
    app: header,
    apis: header,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          // Height-chain link (see revenium.ts VIS-05 note).
          '#root': { height: '100%' },
          body: { lineHeight: 1.5 },
        },
      },
    },
    // nav#header_top.marketing: navy bar with a bright-green bottom border.
    BackstageHeader: {
      styleOverrides: {
        header: {
          boxShadow: 'none',
          borderBottom: `4px solid ${iot.greenBright}`,
        },
        title: { fontFamily: iot.headingFont, fontWeight: 600 },
        subtitle: { fontFamily: iot.headingFont, fontWeight: 400 },
        type: { fontFamily: iot.headingFont },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: ({ theme }: { theme: any }) => ({
          background: theme.palette.navigation?.sidebarGradient,
        }),
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          marginTop: theme.spacing(0.5),
          marginBottom: theme.spacing(0.5),
          '&:hover': {
            backgroundColor: theme.palette.navigation?.navItem?.hoverBackground,
          },
          '&:focus-visible': {
            outline: `2px solid ${iot.greenBright}`,
            outlineOffset: -2,
          },
        }),
        label: {
          fontFamily: iot.headingFont,
          fontSize: '0.95rem',
          fontWeight: 400,
        },
      },
    },
    BackstageSidebarPage: {
      styleOverrides: {
        root: { minHeight: '100vh', display: 'flex', flexDirection: 'column' },
      },
    },
    BackstageContent: {
      styleOverrides: { root: { flex: 1 } },
    },
    // .btn: 10px radius, .85em/1em padding, weight 400, no uppercase on in.gov.
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: iot.radius,
          textTransform: 'none',
          fontWeight: 400,
          fontSize: '1rem',
          padding: '0.6em 1em',
        },
        // .btn-primary (iot): navy, hover → green.
        containedPrimary: {
          '&:hover': { backgroundColor: iot.green },
        },
        // .btn-secondary (iot): green, hover → darker green.
        containedSecondary: {
          '&:hover': { backgroundColor: iot.greenSuccess },
        },
        // .btn-outline-primary: filled on hover.
        outlinedPrimary: {
          borderColor: iot.navy,
          '&:hover': { backgroundColor: iot.navy, color: iot.surface },
        },
      },
    },
    // .card: #fefefe, 1px #e6e6e6 border, 10px radius, no shadow.
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: iot.radius },
        elevation1: { boxShadow: 'none', border: `1px solid ${iot.border}` },
        elevation2: { boxShadow: 'none', border: `1px solid ${iot.border}` },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: iot.radius,
          border: `1px solid ${iot.border}`,
          boxShadow: 'none',
        },
      },
    },
    // .section-header: Readex Pro, 1rem 700, 3px #ddd rule.
    MuiCardHeader: {
      styleOverrides: {
        root: { borderBottom: `3px solid ${iot.rule}` },
        title: {
          fontFamily: iot.headingFont,
          fontSize: '1rem',
          fontWeight: 700,
        },
      },
    },
    // table th on in.gov: navy background, white text.
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          backgroundColor: iot.navy,
          color: iot.surface,
          textTransform: 'none',
          fontFamily: iot.headingFont,
          borderTop: 'none',
          borderBottom: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: { backgroundColor: iot.navy, color: iot.surface },
      },
    },
    // .form-control: 10px radius, blue focus ring rgba(23,121,186,.25).
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: iot.radius,
          '&.Mui-focused': {
            boxShadow: 'rgba(23, 121, 186, 0.25) 0 0 0 0.25rem',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: iot.radius, fontFamily: iot.bodyFont },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: { color: iot.link, '&:hover': { color: iot.linkHover } },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: iot.headingFont,
          fontWeight: 600,
        },
      },
    },
  },
});
